import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDesignContext } from '../hooks/useDesignContext';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.15);
  backdrop-filter: blur(8px);
  z-index: 100000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: wait;
  pointer-events: ${props => props.$visible ? 'all' : 'none'};
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SpinnerWrapper = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
`;

const SpinnerBase = styled.div`
  position: absolute;
  inset: 0;
  border: 4px solid rgba(37, 99, 235, 0.1);
  border-radius: 50%;
`;

const SpinnerActive = styled.div`
  position: absolute;
  inset: 0;
  border: 4px solid transparent;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  box-shadow: 0 0 15px rgba(37, 99, 235, 0.2);
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(0.98); }
`;

const LoadingText = styled.div`
  margin-top: 24px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(4px);
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const GlobalLoader: React.FC = () => {
  const { isGlobalLoading, isApplyingDesign, loadingMessage } = useDesignContext();
  const [canvasPending, setCanvasPending] = useState(0);

  useEffect(() => {
    const handleChange = (e: Event) => {
      const pending: number = (e as CustomEvent).detail?.pending ?? 0;
      setCanvasPending(pending);
    };
    window.addEventListener('canvas:loading:change', handleChange);
    return () => {
      window.removeEventListener('canvas:loading:change', handleChange);
    };
  }, []);

  const isLoading = isGlobalLoading || isApplyingDesign || canvasPending > 0;
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    let showTimeout: number | null = null;
    let hideTimeout: number | null = null;

    if (isLoading) {
      if (isApplyingDesign) {
        // Show immediately when applying a design to hide the "naked" granite fallback
        setShouldShow(true);
      } else {
        // Wait for 200ms for other data fetches to prevent flickering on fast responses
        showTimeout = window.setTimeout(() => setShouldShow(true), 200);
      }
    } else {
      // Keep it visible for 400ms to bridge the gap between loading phases
      hideTimeout = window.setTimeout(() => setShouldShow(false), 400);
    }

    return () => {
      if (showTimeout) window.clearTimeout(showTimeout);
      if (hideTimeout) window.clearTimeout(hideTimeout);
    };
  }, [isLoading]);

  return (
    <Overlay $visible={shouldShow}>
      <SpinnerWrapper>
        <SpinnerBase />
        <SpinnerActive />
      </SpinnerWrapper>
      {shouldShow && <LoadingText>{loadingMessage || 'Loading design...'}</LoadingText>}
    </Overlay>
  );
};

export default GlobalLoader;
